import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, Subscriber } from 'rxjs';
import { AppToastService } from '../../toast/app-toast.service';
import { catchError, map, take } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { FormGroup } from '@angular/forms';
import { PSECredentials } from './models';

export interface B64FileResponse {
  b64data: string;
  filename: string;
}

export interface PreviewResponse {
  text_bedingungen: string;
  text_datenschutz: string;
  text_therapieinhalte: string;
  text_teilnahmeerklaerung: string;
  text_widerruf: string;
  beginn_teilnahmezeitraum: string;
  teilnahmeerklaerung_abrufbar: boolean;
  link_valid_until: string;
  signed: boolean;
  sign_datetime: string;
  canceled: boolean;
  sign_cancel_datetime: string;
  arzt: {
    anrede_titel_nachname: string;
    lanr: string;
    bsnr: string;
    adresse: string;
    telefonnummer: string;
  };
  patient: {
    vorname: string;
    nachname: string;
    versicherten_nummer: string;
    strasse: string;
    hausnummer: string;
    plz: string;
    ort: string;
    land: string;
    geburtsdatum: string;
  };
  krankenkasse: {
    name: string;
    abkuerzung: string;
    logo: string;
  };
}

export interface ArztDatenResponse {
  arzt: {
    anrede_titel_nachname: string;
    lanr: string;
    bsnr: string;
    adresse: string;
    telefonnummer: string;
    praxis_name: string;
  };
  vertrag: {
    name: string;
    text_teilnahmeerklaerung_selbsteinschreibung: string;
    text_teilnahmeerklaerung_datenschutz_selbsteinschreibung: string;
    text_bedingungen_selbsteinschreibung: string;
    text_therapieinhalte_selbsteinschreibung: string;
    text_widerruf_selbsteinschreibung: string;
    tnz_nur_nach_ae: boolean;
  };
  krankenkassen: { name: string; id: number }[];
  vertrag_name: string;
  tnzCheck$: BehaviorSubject<{
    ae: Date;
    vertrag: Date;
    kkBeitritt: Date;
  } | null>;
}

export interface ArztDatenResponseMitInputForm extends ArztDatenResponse {
  input_form: FormGroup;
}

export interface PartnerPreviewResponse extends PreviewResponse {
  partner_einschreibung: {
    vorname: string;
    nachname: string;
    geburtsdatum: string;
  };
}

export interface AvailabilityResponse {
  issue: unknown;
  ae: Date;
  kkBeitritt: Date;
  vertrag: Date;
}

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private _baseURL?: string;
  get baseURL(): string {
    return this._baseURL ?? environment.patienten_selbst_einschreibung_url;
  }
  set baseURL(val: string) {
    this._baseURL = val;
  }

  constructor(
    private store: Store,
    private toastService: AppToastService,
    private httpClient: HttpClient,
  ) {}

  patientenSelbstEinschreibungPreview(credentials: PSECredentials) {
    return this.withBaseUrl<PreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/preview/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PreviewResponse;
          }),
        ),
    );
  }

  partnerSelbstEinschreibungPreview(credentials: {
    link_token: string;
    pin: string;
  }) {
    return this.withBaseUrl<PartnerPreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/partnerselbsteinschreibung/preview/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PartnerPreviewResponse;
          }),
        ),
    );
  }

  patientenSelbstEinschreibungTNE(credentials: {
    link_token?: string;
    pin?: string;
    geburtsdatum?: string;
    versicherten_nummer?: string;
    tnz_beginn?: string;
  }) {
    return this.withBaseUrl<B64FileResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/tne/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as B64FileResponse;
          }),
        ),
    );
  }

  partnerSelbstEinschreibungTNE(credentials: {
    link_token: string;
    pin: string;
  }) {
    return this.withBaseUrl<B64FileResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/partnerselbsteinschreibung/tne/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as B64FileResponse;
          }),
        ),
    );
  }

  patientenSelbstEinschreibungSign(credentials: {
    patient_akzeptiert_datenschutzbestimmungen: boolean;
    patient_akzeptiert_sanakeyportal_datenschutzbestimmungen: boolean;
    patient_akzeptiert_sanakeyportal_nutzungsbedingungen: boolean;
    patient_akzeptiert_teilnahmebedingungen: boolean;
    patient_zustimmung_evaluation: boolean;
    link_token: string;
    pin: string;
  }) {
    return this.withBaseUrl<PreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/sign/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PreviewResponse;
          }),
        ),
    );
  }

  patientenSelbstEinschreibungSignV2(credentials: {
    patient_akzeptiert_datenschutzbestimmungen: boolean;
    patient_akzeptiert_sanakeyportal_datenschutzbestimmungen: boolean;
    patient_akzeptiert_sanakeyportal_nutzungsbedingungen: boolean;
    patient_akzeptiert_teilnahmebedingungen: boolean;
    patient_zustimmung_evaluation: boolean;
    patientenDaten: Record<string, unknown>;
    uuid: string;
    versorgungsvertragId: number;
    vertragsgruppe: number;
    tnz_beginn: string;
  }) {
    return this.withBaseUrl<PreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/signv2/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PreviewResponse;
          }),
        ),
    );
  }

  partnerSelbstEinschreibungSign(credentials: {
    link_token: string;
    pin: string;
    partner_akzeptiert_teilnahmebedingungen: boolean;
    partner_akzeptiert_datenschutzbestimmungen: boolean;
    partner_akzeptiert_sanakeyportal_nutzungsbedingungen: boolean;
    partner_akzeptiert_sanakeyportal_datenschutzbestimmungen: boolean;
  }) {
    return this.withBaseUrl<PartnerPreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/partnerselbsteinschreibung/sign/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PartnerPreviewResponse;
          }),
        ),
    );
  }

  patientenSelbstEinschreibungCancel(credentials: PSECredentials) {
    return this.withBaseUrl<PreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/cancel/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PreviewResponse;
          }),
        ),
    );
  }

  partnerSelbstEinschreibungCancel(credentials: {
    link_token: string;
    pin: string;
  }) {
    return this.withBaseUrl<PartnerPreviewResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/partnerselbsteinschreibung/cancel/`,
          { ...credentials },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as PartnerPreviewResponse;
          }),
        ),
    );
  }

  getArztDaten(
    uuid: string,
    versorgungsVertragID: number,
    vertragsgruppe: number,
  ) {
    return this.withBaseUrl<ArztDatenResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/arztDaten/`,
          { uuid, versorgungsVertragID, vertragsgruppe },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            const ret = r as ArztDatenResponse;
            ret.krankenkassen.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
            return ret;
          }),
        ),
    );
  }

  getVertragDaten(
    uuid: string,
    versorgungsVertragID: number,
    vertragsgruppe: number,
    krankenkasse: number,
  ) {
    return this.withBaseUrl<ArztDatenResponse>(() =>
      this.httpClient
        .post(
          `${this.baseURL}/patientenselbsteinschreibung/vertragDaten/`,
          { uuid, versorgungsVertragID, vertragsgruppe, krankenkasse },
          {
            /* Options */
          },
        )
        .pipe(
          map((r) => {
            return r as ArztDatenResponse;
          }),
        ),
    );
  }

  getKrankenkasseAvailability(
    uuid: string,
    versorgungsVertragID: number,
    vertragsgruppe: number,
    krankenkasseId: number,
  ) {
    const params = new HttpParams()
      .set('uuid', uuid)
      .set('versorgungsVertragID', versorgungsVertragID)
      .set('vertragsgruppe', vertragsgruppe)
      .set('krankenkasseId', krankenkasseId);
    return this.withBaseUrl<AvailabilityResponse>(() =>
      this.httpClient
        .get(
          `${this.baseURL}/patientenselbsteinschreibung/checkKKAvailability/`,
          { params },
        )
        .pipe(
          map((r) => {
            return r as AvailabilityResponse;
          }),
        ),
    );
  }

  getTNZvalid(tnz: string, arztEinschreibungId: number) {
    const params = new HttpParams()
      .set('tnz', tnz)
      .set('arztEinschreibungID', arztEinschreibungId);
    return this.withBaseUrl(() =>
      this.httpClient
        .get(`${this.baseURL}/patientenselbsteinschreibung/checkTNZvalid/`, {
          params,
        })
        .pipe(
          map((r) => {
            return r;
          }),
        )
        .pipe(
          catchError(() => {
            return of(417);
          }),
        ),
    );
  }

  private ensureBaseUrlAndAuthSetAction(
    resolve: (value?: unknown) => void,
    reject: (reason?: unknown) => void,
    counter = 0,
  ) {
    if (this.baseURL !== null) {
      return resolve();
    }
    if (++counter >= 40) {
      // ~ 20 seconds
      return reject();
    }
    setTimeout(
      () => this.ensureBaseUrlAndAuthSetAction(resolve, reject, counter),
      500,
    );
  }

  private ensureBaseUrlSet() {
    return new Promise((resolve, reject) =>
      this.ensureBaseUrlAndAuthSetAction(resolve, reject),
    );
  }

  private withBaseUrl<T>(a: () => Observable<T>) {
    let subscriber: Subscriber<T> = null;
    this.ensureBaseUrlSet().then(() => {
      a().subscribe(subscriber);
    });
    return new Observable<T>((theSubscriber) => {
      subscriber = theSubscriber;
    }).pipe(take(1));
  }
}
